/**
 * Le regard qui se resserre sur le résultat.
 *
 * ## Ce qui change, et pourquoi c'était « aucun mouvement de caméra » jusqu'ici
 *
 * Le § 3.3 suggérait de placer la caméra derrière l'ouvreur. On l'avait écarté deux
 * fois : sur un plateau ROND où le geste attaque de partout, « derrière l'ouvreur »
 * n'a plus de sens, et déplacer la caméra en portrait donne le tournis. Olivier a
 * tranché autrement le 2026-07-26, et il a précisé la forme : **un zoom léger sur le
 * résultat**, façon caméra d'action, avec un peu de rotation. Pas un déplacement.
 *
 * La différence n'est pas de degré : un zoom ne change pas le point de vue, il change
 * la focale. L'œil ne perd pas ses repères, et le tournis vient du déplacement, pas du
 * cadrage.
 *
 * ## Pourquoi la focale et non la position
 *
 * `OrbitControls` écrit la position de la caméra à chaque image, depuis son propre
 * état : bouger la caméra en même temps que lui, c'est se battre avec lui, et le
 * joueur qui fait tourner la scène du doigt gagnerait un tremblement. La **focale**,
 * elle, n'appartient à personne — on peut la resserrer sans rien contredire.
 *
 * Ce module ne contient que la courbe, pour qu'elle se règle et se teste sans WebGL.
 */

/** Durée totale d'un regard, en secondes : resserrement, tenue, retour. */
export const DUREE_REGARD = 2.2;

const RESSERRE = 0.45;
const TENUE = 0.75;

/** Adoucissement classique : départ et arrivée sans à-coup. */
function adoucir(x: number): number {
  const t = Math.min(1, Math.max(0, x));
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}

/**
 * Part du resserrement à l'instant `ecoule`, de 0 (cadrage normal) à 1 (au plus près).
 *
 * Trois temps : on se rapproche, on **tient** — c'est là que le joueur lit ses dés,
 * et sans cette tenue le zoom ne servirait qu'à faire joli —, puis on revient.
 */
export function partDuResserrement(ecoule: number): number {
  if (ecoule <= 0) return 0;
  if (ecoule < RESSERRE) return adoucir(ecoule / RESSERRE);
  if (ecoule < RESSERRE + TENUE) return 1;
  const retour = (ecoule - RESSERRE - TENUE) / (DUREE_REGARD - RESSERRE - TENUE);
  return retour >= 1 ? 0 : 1 - adoucir(retour);
}

/**
 * La focale à cet instant, en degrés.
 *
 * `ampleur` va de 0 (aucun zoom) à 1 (le zoom nominal). Elle sert au repli en
 * mouvement réduit : on ne coupe pas le regard, on le met à zéro, et tout le reste du
 * code continue de tourner sans condition.
 */
export function focaleDuRegard(base: number, ecoule: number, ampleur = 1): number {
  const serrage = ZOOM_MAXIMAL * Math.min(1, Math.max(0, ampleur));
  return base * (1 - serrage * partDuResserrement(ecoule));
}

/**
 * Combien la focale se resserre au plus fort — 16 %.
 *
 * Réglé bas exprès. Olivier a demandé « pas trop violent » : au-delà, le plateau rond
 * sort du cadre en portrait, et on retombe sur le défaut qu'on voulait éviter.
 */
export const ZOOM_MAXIMAL = 0.16;

/**
 * Le léger pivot qui accompagne le zoom, en radians.
 *
 * Il suit la même courbe que la focale — les deux doivent finir ensemble, sinon la
 * scène reste de travers une fois le regard relâché.
 */
export const PIVOT_MAXIMAL = 0.05;

export function pivotDuRegard(ecoule: number, ampleur = 1): number {
  return PIVOT_MAXIMAL * Math.min(1, Math.max(0, ampleur))
    * partDuResserrement(ecoule);
}
